/* ============================================================================
 * useOperationCapture.ts - Operation capture and tracking system
 * ============================================================================ */

import { useCallback, type MutableRefObject } from 'react';
import type { JsonLikeValue, UIOperation } from '@/shared/types/projectData';
import { OPERATION_CONFIG, OperationType } from '@/shared/types';
import { undoStack } from '@/shared/commands/UndoStack';
import { authService } from '@/features/auth/services/authService';
import { log } from '@/shared/utils/logger';

interface UseOperationCaptureParams {
  sessionId: MutableRefObject<string>;
}

function isJsonLikeValueInternal(value: unknown, ancestors: WeakSet<object>): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return true;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (Array.isArray(value)) {
    if (ancestors.has(value)) {
      return false;
    }
    ancestors.add(value);
    const isValid = value.every((entry) => isJsonLikeValueInternal(entry, ancestors));
    ancestors.delete(value);
    return isValid;
  }

  if (typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  if ((prototype !== Object.prototype && prototype !== null) || ancestors.has(value)) {
    return false;
  }

  ancestors.add(value);
  const isValid = Object.values(value).every((entry) => isJsonLikeValueInternal(entry, ancestors));
  ancestors.delete(value);
  return isValid;
}

function isJsonLikeValue(value: unknown): value is JsonLikeValue {
  return isJsonLikeValueInternal(value, new WeakSet());
}

/**
 * Hook for capturing and processing operations
 */
export function useOperationCapture({
  sessionId,
}: UseOperationCaptureParams) {
  
  // Universal operation capture with proper classification system
  const captureOperation = useCallback((
    type: string,
    category: 'view' | 'selection' | 'filter' | 'navigation' | 'preference' | 'interaction' | 'edit',
    before: unknown,
    after: unknown,
    description?: string,
    component?: string,
    skipHistory?: boolean
  ) => {
    if (!isJsonLikeValue(before) || !isJsonLikeValue(after)) {
      log.warn('useOperationCapture', 'Skipping UI operation with non-JSON state', { type, category });
      return;
    }

    // Get operation configuration from classification system
    const config = OPERATION_CONFIG[type] || {
      type: OperationType.LOCAL_INTERACTION, 
      sync: false, 
      history: false 
    };
    
    const operation: UIOperation = {
      type: 'ui-state-change',
      category,
      details: { 
        before,
        after,
        component,
        timestamp: Date.now(),
        operationType: type
      },
      description: description || `${category}: ${type}`,
      timestamp: Date.now(),
      sessionId: sessionId.current,
      userId: authService.getToken() ? 'current-user' : undefined
    };
    
    // Process through undo system (local only) unless skipHistory is true
    if (config.history && !skipHistory) {
      try {
        undoStack.addOperation(operation);
      } catch {
        // Silent fail
      }
    }
    // UI state stays local: the server has no handler for 'ui-state-change', and data changes
    // reach it through applyOp, never through this capture path.
  }, [sessionId]);

  return {
    captureOperation
  };
}
